import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Lock, CheckCircle, AlertCircle, Loader2, KeyRound } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email | code | success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const t = (key) => {
    const texts = {
      title: language === 'es' ? 'Restablecer Contraseña' : 'Reset Password',
      subtitle_email: language === 'es'
        ? 'Introduce tu email y te enviaremos un código de restablecimiento'
        : 'Enter your email and we\'ll send you a reset code',
      subtitle_code: language === 'es'
        ? 'Introduce el código que te enviamos y tu nueva contraseña'
        : 'Enter the code we sent you and your new password',
      email_label: 'Email',
      code_label: language === 'es' ? 'Código de Restablecimiento' : 'Reset Code',
      new_password: language === 'es' ? 'Nueva Contraseña' : 'New Password',
      confirm_password: language === 'es' ? 'Confirmar Contraseña' : 'Confirm Password',
      send_code: language === 'es' ? 'Enviar Código' : 'Send Reset Code',
      reset_password: language === 'es' ? 'Restablecer Contraseña' : 'Reset Password',
      back_login: language === 'es' ? 'Volver al Login' : 'Back to Login',
      success_title: language === 'es' ? '¡Contraseña Restablecida!' : 'Password Reset!',
      success_msg: language === 'es'
        ? 'Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión.'
        : 'Your password has been updated successfully. You can now log in.',
      go_login: language === 'es' ? 'Ir al Login' : 'Go to Login',
      passwords_mismatch: language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match',
      password_min: language === 'es' ? 'La contraseña debe tener al menos 8 caracteres' : 'Password must be at least 8 characters',
      enter_email: language === 'es' ? 'Introduce tu email' : 'Please enter your email',
      enter_code: language === 'es' ? 'Introduce el código' : 'Please enter the code',
      sending: language === 'es' ? 'Enviando...' : 'Sending...',
      resetting: language === 'es' ? 'Restableciendo...' : 'Resetting...',
      didnt_receive: language === 'es' ? '¿No recibiste el código?' : "Didn't receive the code?",
      resend: language === 'es' ? 'Reenviar' : 'Resend',
    };
    return texts[key] || key;
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) { setError(t('enter_email')); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setStep('code');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!code) { setError(t('enter_code')); return; }
    if (newPassword.length < 8) { setError(t('password_min')); return; }
    if (newPassword !== confirmPassword) { setError(t('passwords_mismatch')); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      setStep('success');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError('');
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setError('');
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-8" data-testid="reset-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-[#22c55e]" />
          </div>
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">{t('success_title')}</h2>
          <p className="text-slate-600 mb-8">{t('success_msg')}</p>
          <Link to="/login" className="vf-btn-primary inline-flex items-center justify-center gap-2" data-testid="go-login-btn">
            {t('go_login')}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-8" data-testid="forgot-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1e3a5f] mb-8">
          <ArrowLeft className="w-4 h-4" /> {t('back_login')}
        </Link>

        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#1e3a5f] rounded-sm flex items-center justify-center">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-2">{t('title')}</h2>
          <p className="text-slate-600">
            {step === 'email' ? t('subtitle_email') : t('subtitle_code')}
          </p>
        </div>

        {error && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-700 text-sm">{error}</span>
          </motion.div>
        )}

        {step === 'email' ? (
          <form onSubmit={handleSendCode} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">{t('email_label')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="vf-input w-full pl-12 pr-4 py-3" placeholder="tu@email.com"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="send-code-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('send_code')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">{t('code_label')}</label>
              <input
                type="text" value={code} onChange={e => setCode(e.target.value)} required maxLength={6}
                className="vf-input w-full px-4 py-3 text-center text-2xl tracking-widest" placeholder="000000"
                data-testid="reset-code-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">{t('new_password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  required minLength={8} className="vf-input w-full pl-12 pr-4 py-3" placeholder="••••••••"
                  data-testid="new-password-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">{t('confirm_password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  required minLength={8} className="vf-input w-full pl-12 pr-4 py-3" placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="reset-password-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('reset_password')}
            </button>

            <p className="text-center text-sm text-slate-600">
              {t('didnt_receive')}{' '}
              <button type="button" onClick={handleResend} disabled={loading}
                className="text-[#22c55e] hover:underline disabled:opacity-50">
                {t('resend')}
              </button>
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
